import React, { useEffect, useState } from 'react';
import { tenantService, type Inquilino } from '@/services/tenantService';
import type { Edificio } from '@/services/buildingService';
import { Button } from '@/components/ui/button';
import TenantForm from '@/components/TenantForm';
import { reportError } from '@/lib/errorReporting';
import { toast } from 'sonner';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import { useNavigate } from 'react-router-dom';

const TenantsPage: React.FC = () => {
  const [tenants, setTenants] = useState<Inquilino[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<Inquilino | null>(null);
  const navigate = useNavigate();

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const rawData = await tenantService.getTenants();
      // TODO: Fix this, there should be a best practice to solve this.
      // Cast the raw data to unknown first, then to our expected type
      const data = rawData as unknown as Array<{
        id_inquilino: string;
        id_unidad: string;
        nombre_completo: string;
        email: string | null;
        telefono: string | null;
        fecha_inicio_contrato: string | null;
        fecha_fin_contrato: string | null;
        activo: boolean;
        unidad: {
          id_edificio: string;
          numero_unidad: string;
          edificio_unidad: Edificio;
        };
      }>;

      const typedData: Inquilino[] = data.map(tenant => ({
        id_inquilino: tenant.id_inquilino,
        id_unidad: tenant.id_unidad,
        nombre_completo: tenant.nombre_completo,
        email: tenant.email ?? undefined,
        telefono: tenant.telefono ?? undefined,
        fecha_inicio_contrato: tenant.fecha_inicio_contrato ?? undefined,
        fecha_fin_contrato: tenant.fecha_fin_contrato ?? undefined,
        activo: tenant.activo,
        unidad: tenant.unidad,
        edificio_unidad: tenant.unidad.edificio_unidad
      }));

      setTenants(typedData);
    } catch (error) {
      reportError(error, {
        component: 'TenantsPage',
        action: 'fetchTenants',
        level: 'error'
      });
      setError(error instanceof Error ? error.message : 'Error desconocido');
      toast.error("Error al cargar los inquilinos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleSaveTenant = async (tenant: Inquilino) => {
    try {
      if (tenant.id_inquilino) {
        await tenantService.updateTenant(tenant.id_inquilino, tenant);
        toast.success("Inquilino actualizado exitosamente.");
      } else {
        await tenantService.createTenant(tenant);
        toast.success("Inquilino creado exitosamente.");
      }
      fetchTenants();
    } catch (error) {
      reportError(error, {
        component: 'TenantsPage',
        action: tenant.id_inquilino ? 'updateTenant' : 'createTenant',
        extra: {
          tenantId: tenant.id_inquilino,
          tenantData: tenant
        },
        level: 'error'
      });
      toast.error("Error al guardar el inquilino");
    }
  };

  const handleEdit = (tenant: Inquilino) => {
    setCurrentTenant(tenant);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar este inquilino?")) {
      try {
        await tenantService.deleteTenant(id);
        toast.success("Inquilino eliminado exitosamente.");
        fetchTenants();
      } catch (error) {
        reportError(error, {
          component: 'TenantsPage',
          action: 'deleteTenant',
          extra: {
            tenantId: id
          },
          level: 'error'
        });
        toast.error("Error al eliminar el inquilino");
      }
    }
  };

  const handleToggleActive = async (tenant: Inquilino) => {
    if (!tenant.id_inquilino) {
      toast.error("ID de inquilino no válido");
      return;
    }

    try {
      await tenantService.updateTenant(tenant.id_inquilino, { activo: !tenant.activo });
      toast.success(`Inquilino ${tenant.activo ? 'desactivado' : 'activado'} exitosamente.`);
      fetchTenants();
    } catch (error) {
      reportError(error, {
        component: 'TenantsPage',
        action: 'toggleTenantActive',
        extra: {
          tenantId: tenant.id_inquilino,
          currentActiveState: tenant.activo
        },
        level: 'error'
      });
      toast.error("Error al cambiar el estado del inquilino");
    }
  };

  const breadcrumbs = [
    { label: "Inicio", path: "/dashboard" },
    { label: "Inquilinos", path: "/tenants" },
  ];

  if (loading) {
    return <div>Cargando inquilinos...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <Breadcrumbs items={breadcrumbs} />
      <h1 className="text-2xl font-bold mb-4">Gestión de Inquilinos</h1>
      <Button className="mb-4" onClick={() => { setCurrentTenant(null); setIsFormOpen(true); }}>Agregar Nuevo Inquilino</Button>
      {tenants.length === 0 ? (
        <p>No hay inquilinos registrados.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenants.map((tenant) => (
            <div key={tenant.id_inquilino} className="border p-4 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold">{tenant.nombre_completo}</h2>
              <p className="text-gray-600">Email: {tenant.email}</p>
              <p className="text-gray-600">Teléfono: {tenant.telefono}</p>
              {tenant.unidad && tenant.edificio_unidad && (
                <p className="text-gray-600">Edificio: {tenant.edificio_unidad.nombre_edificio} - Unidad: {tenant.unidad.numero_unidad}</p>
              )}
              <p className="text-gray-600">Activo: {tenant.activo ? 'Sí' : 'No'}</p>
              <div className="mt-4 flex space-x-2">
                <Button variant="outline" onClick={() => navigate(`/tenants/${tenant.id_inquilino}`)}>Ver Detalles</Button>
                <Button variant="secondary" onClick={() => handleEdit(tenant)}>Editar</Button>
                <Button variant="destructive" onClick={() => handleDelete(tenant.id_inquilino!)}>Eliminar</Button>
                <Button variant="outline" onClick={() => handleToggleActive(tenant)}>
                  {tenant.activo ? 'Desactivar' : 'Activar'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <TenantForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSaveTenant}
        initialData={currentTenant}
      />
    </div>
  );
};

export default TenantsPage;
